import { IconQR } from 'components/atoms/icons/IconQR';
import { Fragment, ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import CollectRouteBookingQRScanner from 'components/molecules/CollectRouteBookingQRScanner';
import IconListOutline from 'components/atoms/icons/IconListOutline';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import { IconClose } from 'components/atoms/icons/IconClose';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import { ConfirmSelectEmissionItemsModal } from '../ConfirmSelectEmissionItemsModal';
import { mergeClasses } from 'util/commons';

export type QRScanItemDisplay = {
  value: string;
  unit?: string;
};

// What to show in the "last scanned" panel on the left
export type QRScanLatestResultDisplay = {
  label?: string; // descriptor above the name (e.g. "Item name")
  name: string; // primary title shown large
  value: string; // numeric value shown below
  unit?: string; // unit label (e.g. "kg")
};

// Data needed by the standard confirm modal
export type QRScanConfirmData = {
  quantity: number;
  totalWeight: number;
  items: Record<string, { name: string; weight: string }>;
};

export type QRScanGroupItem = {
  id: string;
};

export type QRScanGroup<TItem extends QRScanGroupItem = QRScanGroupItem> = {
  id: string;
  name: string;
  data: TItem[];
};

export type QRScanningSubmitParams<
  TGroupItem extends QRScanGroupItem = QRScanGroupItem
> = {
  checkedItemIds: string[];
  groupedItems: QRScanGroup<TGroupItem>[];
  isCompleted: boolean;
  onSuccess: () => void;
};

export type QRScanningProps<
  TScanResult,
  TGroupItem extends QRScanGroupItem = QRScanGroupItem
> = {
  onScan: (qrCode: string, addItem: (data: TScanResult) => void) => void;
  onSubmit?: (params: QRScanningSubmitParams<TGroupItem>) => void;
  getItemId: (data: TScanResult) => string;
  /** When omitted, the scan result itself is stored as the group item (requires TScanResult to have `id`). */
  toGroupItem?: (data: TScanResult) => TGroupItem;
  /**
   * When provided: items are grouped by the returned id and displayed under a group header.
   * When omitted: flat mode — each scanned item is displayed individually with no grouping.
   */
  getGroupId?: (data: TScanResult) => string;
  /** Required when `getGroupId` is provided. */
  getGroupName?: (data: TScanResult) => string;
  getLatestScanDisplay?: (
    data: TScanResult
  ) => QRScanLatestResultDisplay | null;
  renderLatestScanResult?: (data: TScanResult) => ReactNode;
  getGroupItemDisplay?: (item: TGroupItem) => QRScanItemDisplay;
  renderGroupItem?: (params: {
    item: TGroupItem;
    isChecked: boolean;
    onToggle: () => void;
  }) => ReactNode;
  renderGroup?: (params: {
    group: QRScanGroup<TGroupItem>;
    checkedItemIds: string[];
    checkedItems: TGroupItem[];
    onToggleItem: (itemId: string) => void;
    onToggleGroup: (group: QRScanGroup<TGroupItem>) => void;
    isGroupChecked: boolean;
  }) => ReactNode;
  /** Override only the group header; items below are still rendered normally. */
  renderGroupHeader?: (params: {
    group: QRScanGroup<TGroupItem>;
    checkedItemIds: string[];
    checkedItems: TGroupItem[];
    onToggleItem: (itemId: string) => void;
    onToggleGroup: (group: QRScanGroup<TGroupItem>) => void;
    isGroupChecked: boolean;
  }) => ReactNode;
  getGroupSummaryDisplay?: (params: {
    group: QRScanGroup<TGroupItem>;
    checkedItemIds: string[];
    checkedItems: TGroupItem[];
  }) => QRScanItemDisplay | null;
  renderGroupSummary?: (params: {
    group: QRScanGroup<TGroupItem>;
    checkedItemIds: string[];
    checkedItems: TGroupItem[];
  }) => ReactNode;
  getConfirmData?: (params: {
    checkedItemIds: string[];
    groupedItems: QRScanGroup<TGroupItem>[];
    checkedItems: TGroupItem[];
  }) => QRScanConfirmData | null;
  renderConfirmModal?: (params: {
    checkedItemIds: string[];
    groupedItems: QRScanGroup<TGroupItem>[];
    checkedItems: TGroupItem[];
    onConfirm: (isCompleted?: boolean) => void;
    onClose: () => void;
  }) => ReactNode;
  renderGroupAction?: (group: QRScanGroup<TGroupItem>) => ReactNode;
  renderBottomActions?: (params: {
    checkedItemIds: string[];
    groupedItems: QRScanGroup<TGroupItem>[];
    checkedItems: TGroupItem[];
    isDisabled: boolean;
    openConfirmModal: () => void;
    submit: (isCompleted?: boolean) => void;
  }) => ReactNode;
  title?: string;
  finishText?: string;
  onClose?: () => void;
  paused?: boolean;
  onFinishButtonClick?: (params: {
    groupedQRCode: QRScanGroup<TGroupItem>[];
    checkedItemIds: string[];
    checkedItems: TGroupItem[];
    openConfirmModal: () => void;
  }) => void;
  /**
   * Controls how check/uncheck works:
   * - 'item' (default): each item toggled individually
   * - 'group': clicking the group header toggles all items; items are display-only
   * - 'both': group header toggles all + each item is still individually clickable
   */
  checkMode?: 'item' | 'group' | 'both';
  /**
   * Controls whether confirm modal should be shown when onSubmit is called.
   */
  showConfirmModal?: boolean;
  classNames?: {
    scannedList?: string;
  };
};

function QRScanning<TScanResult, TGroupItem extends QRScanGroupItem>({
  onScan,
  onSubmit,
  getItemId,
  toGroupItem,
  getGroupId,
  getGroupName,
  getLatestScanDisplay,
  renderLatestScanResult,
  getGroupItemDisplay,
  renderGroupItem,
  renderGroup,
  renderGroupHeader,
  getGroupSummaryDisplay,
  renderGroupSummary,
  getConfirmData,
  renderConfirmModal,
  renderGroupAction,
  renderBottomActions,
  title,
  finishText,
  onClose,
  paused,
  onFinishButtonClick,
  checkMode = 'item',
  showConfirmModal,
  classNames,
}: QRScanningProps<TScanResult, TGroupItem>) {
  const { t } = useTranslation();

  const [latestScanResult, setLatestScanResult] = useState<TScanResult | null>(
    null
  );
  // Tracks raw QR code strings to prevent re-scanning the same code.
  const scannedQRCodes = useRef<Set<string>>(new Set());
  // Maps itemId → qrCode so we only remove submitted items' codes on success.
  const itemIdToQRCode = useRef<Map<string, string>>(new Map());
  const [groupedQRCode, setGroupedQRCode] = useState<QRScanGroup<TGroupItem>[]>(
    []
  );
  const [checkedItemIds, setCheckedItemIds] = useState<Set<string>>(new Set());
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);

  const checkedGroupItems = useMemo(
    () =>
      groupedQRCode.flatMap((group) =>
        group.data.filter((item) => checkedItemIds.has(item.id))
      ),
    [groupedQRCode, checkedItemIds]
  );

  const handleAddToGroup = (data: TScanResult) => {
    const itemId = getItemId(data);

    // Flat mode: each item is its own group keyed by item id
    const groupId = getGroupId ? getGroupId(data) : itemId;

    setCheckedItemIds((prev) => new Set(prev).add(itemId));

    setGroupedQRCode((prevGrouped) => {
      const groupIndex = prevGrouped.findIndex((g) => g.id === groupId);
      const groupItem = toGroupItem
        ? toGroupItem(data)
        : (data as unknown as TGroupItem);

      if (groupIndex === -1) {
        return [
          ...prevGrouped,
          {
            id: groupId,
            name: getGroupName ? getGroupName(data) : '',
            data: [groupItem],
          },
        ];
      }

      if (prevGrouped[groupIndex].data.some((item) => item.id === itemId)) {
        return prevGrouped;
      }

      const updatedGroups = [...prevGrouped];
      updatedGroups[groupIndex] = {
        ...updatedGroups[groupIndex],
        data: [...updatedGroups[groupIndex].data, groupItem],
      };
      return updatedGroups;
    });
  };

  const handleScanSuccess = (qrCode: string) => {
    if (!qrCode) return;
    if (scannedQRCodes.current.has(qrCode)) return;
    scannedQRCodes.current.add(qrCode);
    onScan(qrCode, (data) => {
      itemIdToQRCode.current.set(getItemId(data), qrCode);
      setLatestScanResult(data);
      handleAddToGroup(data);
    });
  };

  const handleToggleItem = (itemId: string) => {
    setCheckedItemIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const isGroupFullyChecked = (group: QRScanGroup<TGroupItem>) =>
    group.data.length > 0 &&
    group.data.every((item) => checkedItemIds.has(item.id));

  const handleToggleGroup = (group: QRScanGroup<TGroupItem>) => {
    const isChecked = isGroupFullyChecked(group);
    setCheckedItemIds((prev) => {
      const newSet = new Set(prev);
      group.data.forEach((item) => {
        if (isChecked) {
          newSet.delete(item.id);
        } else {
          newSet.add(item.id);
        }
      });
      return newSet;
    });
  };

  const totalItemCount = useMemo(
    () => groupedQRCode.reduce((total, group) => total + group.data.length, 0),
    [groupedQRCode]
  );

  const handleSubmit = (isCompleted = false) => {
    const checkedItemIdsArray = Array.from(checkedItemIds);

    const onSuccess = () => {
      setIsShowConfirmModal(false);
      // Only remove QR codes for submitted items; remaining items stay protected.
      checkedItemIdsArray.forEach((id) => {
        const qrCode = itemIdToQRCode.current.get(id);
        if (qrCode) scannedQRCodes.current.delete(qrCode);
        itemIdToQRCode.current.delete(id);
      });
      setGroupedQRCode((prevGroups) =>
        prevGroups
          .map((group) => ({
            ...group,
            data: group.data.filter(
              (item) => !checkedItemIdsArray.includes(item.id)
            ),
          }))
          .filter((group) => group.data.length > 0)
      );
      setCheckedItemIds(new Set());
    };

    onSubmit?.({
      checkedItemIds: checkedItemIdsArray,
      groupedItems: groupedQRCode,
      isCompleted,
      onSuccess,
    });
  };

  const openConfirmModal = () => {
    if (showConfirmModal && (getConfirmData || renderConfirmModal)) {
      setIsShowConfirmModal(true);
    } else {
      handleSubmit();
    }
  };

  const handleFinishButtonClick = () => {
    if (onFinishButtonClick) {
      onFinishButtonClick({
        groupedQRCode,
        checkedItemIds: Array.from(checkedItemIds),
        openConfirmModal,
        checkedItems: checkedGroupItems,
      });
    } else {
      openConfirmModal();
    }
  };

  const latestScanDisplay =
    latestScanResult != null && !renderLatestScanResult
      ? getLatestScanDisplay?.(latestScanResult)
      : null;

  const confirmData =
    isShowConfirmModal && !renderConfirmModal
      ? getConfirmData?.({
          checkedItemIds: Array.from(checkedItemIds),
          groupedItems: groupedQRCode,
          checkedItems: checkedGroupItems,
        })
      : null;

  return (
    <div className={styles.qrScanning}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.iconQR}>
            <IconQR />
          </div>
          <div>
            <p className={styles.title}>{title ?? t('qr_scan.title')}</p>
          </div>
        </div>
        {onClose && (
          <div className={styles.closeButton} onClick={onClose}>
            <IconClose />
          </div>
        )}
      </div>

      <div className={styles.scannerContainter}>
        <div className={styles.scannerLeft}>
          <CollectRouteBookingQRScanner
            successCallback={handleScanSuccess}
            paused={paused}
          />

          {latestScanResult != null && renderLatestScanResult
            ? renderLatestScanResult(latestScanResult)
            : latestScanDisplay && (
                <div className={styles.scannerResult}>
                  <p className={styles.scanSuccessText}>
                    {t('qr_scan.success')}
                  </p>
                  {latestScanDisplay.label && (
                    <p className={styles.scanItemNameLabel}>
                      {latestScanDisplay.label}
                    </p>
                  )}
                  <p className={styles.scanItemName}>
                    {latestScanDisplay.name}
                  </p>
                  <p>
                    <span className={styles.scanItemWeight}>
                      {latestScanDisplay.value}
                    </span>
                    {latestScanDisplay.unit && (
                      <span className={styles.scanItemUnit}>
                        {latestScanDisplay.unit}
                      </span>
                    )}
                  </p>
                </div>
              )}
        </div>

        <div className={styles.scannerRight}>
          <div className={styles.scannedListTop}>
            <div className={styles.scannedListTitleContainer}>
              <IconListOutline color="#9f9f9f" />
              <span className={styles.scannedListTitle}>
                {t('qr_scan.scanned_list')}
              </span>
            </div>
            <div>
              <span className={styles.scannedListCount}>
                {totalItemCount}&nbsp;
              </span>
              {t('qr_scan.items')}
            </div>
          </div>

          <div className={styles.scrollContents}>
            <div
              className={mergeClasses(
                styles.scannedList,
                classNames?.scannedList
              )}
            >
              {groupedQRCode.length > 0 ? (
                groupedQRCode.map((group, index) => {
                  // renderGroup: full override of any single group block
                  if (renderGroup) {
                    return (
                      <Fragment key={index}>
                        {renderGroup({
                          group,
                          checkedItemIds: Array.from(checkedItemIds),
                          checkedItems: checkedGroupItems,
                          onToggleItem: handleToggleItem,
                          onToggleGroup: handleToggleGroup,
                          isGroupChecked: isGroupFullyChecked(group),
                        })}
                      </Fragment>
                    );
                  }

                  // Shared item renderer (used in both grouped and flat modes)
                  const renderItem = (item: TGroupItem) => {
                    const isChecked = checkedItemIds.has(item.id);

                    if (renderGroupItem) {
                      return (
                        <Fragment key={item.id}>
                          {renderGroupItem({
                            item,
                            isChecked,
                            onToggle: () => handleToggleItem(item.id),
                          })}
                        </Fragment>
                      );
                    }
                    const display = getGroupItemDisplay?.(item);
                    const itemClickable =
                      checkMode === 'item' || checkMode === 'both';
                    return (
                      <Fragment key={item.id}>
                        <div
                          className={styles.scannedListItemBottomChildren}
                          onClick={
                            itemClickable
                              ? () => handleToggleItem(item.id)
                              : undefined
                          }
                          style={{
                            cursor: itemClickable ? 'pointer' : 'default',
                            opacity: isChecked ? 1 : 0.5,
                          }}
                        >
                          {display && (
                            <p>
                              <span className={styles.value}>
                                {display.value}&nbsp;
                              </span>
                              {display.unit && (
                                <span className={styles.unit}>
                                  {display.unit}
                                </span>
                              )}
                            </p>
                          )}
                          {itemClickable &&
                            (isChecked ? (
                              <div className={styles.iconCheck}>
                                <IconCheck size={20} />
                              </div>
                            ) : (
                              <IconEyeSlash size={26} />
                            ))}
                        </div>
                      </Fragment>
                    );
                  };

                  // Flat mode: no group header, each item renders directly
                  if (!getGroupId) {
                    return (
                      <Fragment key={index}>
                        {group.data.map((item) => renderItem(item))}
                      </Fragment>
                    );
                  }

                  // Grouped mode: group header + item rows
                  const isGroupChecked = isGroupFullyChecked(group);
                  const summary = !renderGroupSummary
                    ? getGroupSummaryDisplay?.({
                        group,
                        checkedItems: checkedGroupItems,
                        checkedItemIds: Array.from(checkedItemIds),
                      })
                    : null;

                  const groupRenderParams = {
                    group,
                    checkedItems: checkedGroupItems,
                    checkedItemIds: Array.from(checkedItemIds),
                    onToggleItem: handleToggleItem,
                    onToggleGroup: handleToggleGroup,
                    isGroupChecked,
                  };

                  return (
                    <div
                      key={index}
                      className={styles.scannedListItemContainer}
                    >
                      {renderGroupHeader ? (
                        renderGroupHeader(groupRenderParams)
                      ) : (
                        <div className={styles.scannedListItemTop}>
                          <div className={styles.scannedListItemTitle}>
                            <div
                              className={styles.scannedListItemLeftTitle}
                              onClick={
                                checkMode === 'group' || checkMode === 'both'
                                  ? () => handleToggleGroup(group)
                                  : undefined
                              }
                              style={{
                                cursor:
                                  checkMode === 'group' || checkMode === 'both'
                                    ? 'pointer'
                                    : 'default',
                                opacity:
                                  (checkMode === 'group' ||
                                    checkMode === 'both') &&
                                  !isGroupChecked
                                    ? 0.5
                                    : 1,
                              }}
                            >
                              {checkMode === 'group' || checkMode === 'both' ? (
                                <div
                                  style={{
                                    marginRight: 10,
                                  }}
                                >
                                  {isGroupChecked ? (
                                    <div className={styles.iconGroupCheck}>
                                      <IconCheck size={20} />
                                    </div>
                                  ) : (
                                    <IconEyeSlash size={26} />
                                  )}
                                </div>
                              ) : (
                                <span className={styles.dot} />
                              )}
                              <span className={styles.scannedListItemName}>
                                {group.name}
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: 8,
                              }}
                            >
                              {renderGroupAction?.(group)}
                            </div>
                          </div>
                          {renderGroupSummary
                            ? renderGroupSummary({
                                group,
                                checkedItems: checkedGroupItems,
                                checkedItemIds: Array.from(checkedItemIds),
                              })
                            : summary && (
                                <div>
                                  <span className={styles.scannedListCount}>
                                    {summary.value}&nbsp;
                                  </span>
                                  {summary.unit && (
                                    <span
                                      className={styles.scannedListItemUnit}
                                    >
                                      {summary.unit}
                                    </span>
                                  )}
                                </div>
                              )}
                        </div>
                      )}
                      <div className={styles.scannedListItemBottom}>
                        {group.data.map((item) => renderItem(item))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={styles.noData}>{t('messages.M_008')}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.finishButtonContainer}>
        {renderBottomActions ? (
          renderBottomActions({
            checkedItemIds: Array.from(checkedItemIds),
            checkedItems: checkedGroupItems,
            groupedItems: groupedQRCode,
            isDisabled: groupedQRCode.length === 0 || checkedItemIds.size === 0,
            openConfirmModal,
            submit: handleSubmit,
          })
        ) : (
          <button
            className={styles.finishButton}
            disabled={groupedQRCode.length === 0 || checkedItemIds.size === 0}
            onClick={handleFinishButtonClick}
          >
            <div className={styles.finishButtonContent}>
              <div className={styles.finishButtonIcon}>
                <IconCheck size={20} fillColor="#000" borderColor="#000" />
              </div>
              {finishText ?? t('qr_scan.finish_scanning')}
            </div>
          </button>
        )}
      </div>

      {isShowConfirmModal && renderConfirmModal
        ? renderConfirmModal({
            checkedItemIds: Array.from(checkedItemIds),
            checkedItems: checkedGroupItems,
            groupedItems: groupedQRCode,
            onConfirm: handleSubmit,
            onClose: () => setIsShowConfirmModal(false),
          })
        : confirmData && (
            <ConfirmSelectEmissionItemsModal
              quantity={confirmData.quantity}
              totalWeight={confirmData.totalWeight}
              items={confirmData.items}
              onSelectItems={handleSubmit}
              onClose={() => setIsShowConfirmModal(false)}
            />
          )}
    </div>
  );
}

export default QRScanning;
